'use client';

import { useCallback, useRef, useState } from 'react';
import type { AudioRecordingFile, RecordingStatus } from '../types';

type StartRecordingFailureReason = 'permission-denied' | 'consent-cancelled';

interface StartRecordingResult {
  success: boolean;
  failureReason?: StartRecordingFailureReason;
}

export function useAudioRecorder() {
  const [status, setStatus] = useState<RecordingStatus>('idle');
  const [duration, setDuration] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);

  const startRecording = useCallback(
    async (onMicrophonePermissionGranted?: () => Promise<boolean>): Promise<StartRecordingResult> => {
      let stream: MediaStream | null = null;

      const stopStream = () => {
        if (!stream) return;
        for (const track of stream.getTracks()) {
          track.stop();
        }
        stream = null;
        streamRef.current = null;
      };

      try {
        setStatus('requesting');
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;

        if (onMicrophonePermissionGranted) {
          const canContinue = await onMicrophonePermissionGranted();
          if (!canContinue) {
            stopStream();
            setStatus('idle');
            return { success: false, failureReason: 'consent-cancelled' };
          }
        }

        if (!stream) {
          throw new Error('Microphone stream is unavailable');
        }

        const recordingStream = stream;
        const mimeType = MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : 'audio/webm';
        const mediaRecorder = new MediaRecorder(recordingStream, { mimeType });
        mediaRecorderRef.current = mediaRecorder;
        chunksRef.current = [];

        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunksRef.current.push(e.data);
          }
        };

        mediaRecorder.start();
        startTimeRef.current = Date.now();
        setStatus('recording');
        setDuration(0);

        timerRef.current = setInterval(() => {
          setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }, 1000);

        return { success: true };
      } catch (_error) {
        stopStream();
        setStatus('error');
        return { success: false, failureReason: 'permission-denied' };
      }
    },
    [],
  );

  const stopRecording = useCallback((): Promise<AudioRecordingFile | null> => {
    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current;
      if (!mediaRecorder || mediaRecorder.state === 'inactive') {
        resolve(null);
        return;
      }

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType });
        const url = URL.createObjectURL(blob);
        const finalDuration = Math.floor((Date.now() - startTimeRef.current) / 1000);

        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        if (streamRef.current) {
          for (const track of streamRef.current.getTracks()) {
            track.stop();
          }
          streamRef.current = null;
        }

        setStatus('stopped');
        resolve({
          blob,
          url,
          duration: finalDuration,
          size: blob.size,
        });
      };

      mediaRecorder.stop();
    });
  }, []);

  const reset = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        track.stop();
      }
      streamRef.current = null;
    }
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current = null;
    }
    chunksRef.current = [];
    setStatus('idle');
    setDuration(0);
  }, []);

  return {
    status,
    duration,
    startRecording,
    stopRecording,
    reset,
  };
}
